/**
 * Step 2 of the analyser: where the client stands with no plan, as four numbers and the story.
 *
 * THE NUMBERS ARE THE HEADLINE, THE STORY IS THE EXPLANATION. The plain-words note ("Where Joe
 * stands with no plan") is the clearest writing on the page and used to sit inside the inputs card,
 * below the fields, with the same figures repeated on the plan summary beside it. Here the four
 * numbers a client asks about come first — what care costs, the pension, what is left to find each
 * year, and what is left to the family — and the note follows, unchanged, to say why each is what it
 * is. No plan summary is shown on this step: this IS the baseline, and a rail restating it was one
 * more column saying the same thing.
 *
 * THREE CARDS, ONE WIDTH (owner, 22 Sep 2026: the note "looks a bit off" nested inside the tiles'
 * card). The numbers, the story and the money timeline are three sibling cards of the same width,
 * so nothing is a card inside a card.
 *
 * ONE SOURCE. The tiles read the same walk-in baseline the note reads (`useWalkInBaseline`) and the
 * same no-plan projection the plan summary measures every strategy against (`agedCareBaseline`),
 * so a figure here is the figure everywhere else.
 */
import { stayPutHeading, stayPutIntro } from './facilityMoveWords.ts';
import { phasesIntro, phaseOneCaption, phaseTwoHeading, phaseFromLabel, roleNames } from './carePhases.ts';
import { runwayFactsOf } from './shortfallOutlook.ts';
import React, { useMemo, useState } from 'react';
import { ProjectionYearsInput } from '../../components/ui/ProjectionYearsInput.tsx';
import { ArrowRight, BookOpen, ChevronLeft, Eye } from 'lucide-react';
import type { ExistingStayForWalkIn } from './WalkInPositionNote.tsx';
import WalkInPositionNote, { useWalkInBaseline } from './WalkInPositionNote.tsx';
import type { AgedCareBaseline } from './agedCareBaseline.ts';
import { FundingRunway } from '../../components/narrative/FundingRunway.tsx';
import type { PlanRunwayCopy } from '../../components/insights/fundingSummary.tsx';
import { GradientText } from '../../components/ui/index.tsx';
import CalculationTooltip, { type TooltipBreakdownRow } from '../../components/ui/CalculationTooltip.tsx';
import { buildMoneySequence, HorizonNote, type RunwayRow } from './moneySequence.tsx';
import { formatCurrency } from '../../utils/index.ts';
import { RATES_PROVENANCE } from '../../../../shared/src/calculation/constants/ratesAndThresholds.ts';
import type { DraftFactFind } from '../../constants/initialFactFind.ts';

interface WalkInHeroProps {
    factFind: DraftFactFind;
    radInput: number;
    /** The second room, where both of a couple enter care — priced beside the first. */
    secondRoom?: { person: 'client' | 'partner'; roomPrice: number; careStartDate?: string; extraServiceAnnual?: number; entersLater?: boolean; livingExpensesOnceBoth?: number } | null;
    /** Someone in care now: the position is staying put in their current home (8 Oct 2026). */
    existingStay?: ExistingStayForWalkIn | null;
    /** Leaving a retirement village (8 Oct 2026): with no plan the exit payment is banked when it arrives. */
    villageExit?: { entitlement: number; refundDate: string } | null;
    extraServiceFeeInput: number;
    scenarioLivingExpenses: number;
    /** The no-plan projection the page measures against: the estate figure and the runway come from it. */
    agedCareBaseline: AgedCareBaseline;
    /** The runway struck on the no-plan projection — the same helper the plan summary uses. */
    runway: PlanRunwayCopy | null;
    legacyYears: number;
    /**
     * Lets the reader change the period on the tile and watch the estate move (owner, 25 Sep 2026:
     * the village does this and every module framed the same way should). Omitted, the caption is
     * plain text, so a read-only surface loses nothing.
     */
    onLegacyYearsChange?: (years: number) => void;
    onBack: () => void;
    onNext: () => void;
    onReadGuide: () => void;
    onAssumptions: () => void;
    /** Opens the projections for THIS position over the legacy horizon (owner, 22 Sep 2026). */
    onShowProjections: () => void;
}

const CARD = 'rounded-2xl border border-white/80 bg-white/60 p-6 shadow-lg shadow-gray-200/50 backdrop-blur-xl';

const Tile = ({
    label,
    value,
    unit,
    caption,
    tone,
    onFigureClick,
    figureTooltip,
    figureOpen,
}: {
    label: string;
    value: string;
    unit?: string;
    caption: React.ReactNode;
    tone: 'cost' | 'good' | 'bad' | 'family';
    /** When set, the figure is a control that opens its breakdown (the daily cost). */
    onFigureClick?: () => void;
    figureTooltip?: TooltipBreakdownRow[];
    figureOpen?: boolean;
}) => {
    const rule = { cost: 'bg-slate-500', good: 'bg-emerald-400', bad: 'bg-rose-400', family: 'bg-[#0290F5]' }[tone];
    const figure = { cost: 'text-slate-900', good: 'text-emerald-700', bad: 'text-rose-700', family: 'text-slate-900' }[tone];
    const figureNode = (
        <span className={`text-[28px] font-bold leading-none tabular-nums tracking-tight ${figure}`}>
            {value}
            {unit && <span className="ml-1 text-[13px] font-semibold text-slate-500">{unit}</span>}
        </span>
    );
    return (
        <div className={`relative overflow-hidden rounded-xl border bg-white px-4 pb-4 pt-5 shadow-card ${figureOpen ? 'border-blue-400 ring-2 ring-blue-100' : 'border-slate-200'}`}>
            <span className={`absolute inset-x-0 top-0 h-1 ${rule}`} aria-hidden />
            <p className="text-[13px] font-semibold text-slate-600">{label}</p>
            <div className="mt-1.5">
                {onFigureClick ? (
                    <CalculationTooltip title="Daily cost — how it is made up" data={figureTooltip}>
                        <button
                            type="button"
                            onClick={onFigureClick}
                            aria-expanded={!!figureOpen}
                            className="inline-block cursor-pointer rounded-sm text-left underline decoration-dotted decoration-slate-400 underline-offset-4 hover:decoration-blue-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
                        >
                            {figureNode}
                        </button>
                    </CalculationTooltip>
                ) : (
                    figureNode
                )}
            </div>
            <p className="mt-2 text-[13px] leading-snug text-slate-600">{caption}</p>
        </div>
    );
};

const WalkInHero = ({
    factFind,
    radInput,
    secondRoom = null,
    existingStay = null,
    villageExit = null,
    extraServiceFeeInput,
    scenarioLivingExpenses,
    agedCareBaseline,
    runway,
    legacyYears,
    onLegacyYearsChange,
    onBack,
    onNext,
    onReadGuide,
    onAssumptions,
    onShowProjections,
}: WalkInHeroProps) => {
    const { costs, secondCosts, careFinancials, todayFinancials, phase2 } = useWalkInBaseline(factFind, radInput, extraServiceFeeInput, scenarioLivingExpenses, secondRoom, existingStay);
    const [costOpen, setCostOpen] = useState(false);
    const goals = (factFind.goals || {}) as { aged_care_for?: string };
    const bothEntering = factFind.adviceFor === 'couple' && goals.aged_care_for === 'both' && !!secondRoom && secondRoom.roomPrice > 0;
    const names = roleNames(factFind, secondRoom?.person);
    const enteringName = bothEntering
        ? `${names.firstName} and ${names.secondName}`
        : (goals.aged_care_for === 'partner' ? factFind.partner?.firstName : factFind.client?.firstName) || 'the client';
    const ready = (radInput > 0 || !!existingStay) && !!costs && !!careFinancials && !!todayFinancials;

    // Both rooms together, where both enter together: the figure the household actually faces. A
    // second person entering LATER is the second stage (phase2), shown as its own row beneath.
    const dailyTotal = ready ? (costs!.totalDailyCost || 0) + (bothEntering && !phase2 ? (secondCosts?.totalDailyCost || 0) : 0) : 0;
    const phaseNames = { ...names, fromLabel: phaseFromLabel(phase2?.fromDate || '') };
    const phaseTwoDaily = phase2 ? (phase2.firstCosts.totalDailyCost || 0) + (phase2.secondCosts?.totalDailyCost || 0) : 0;
    const phaseTwoPension = phase2 ? phase2.financials.socialSecurity || 0 : 0;
    const phaseTwoGap = phase2 ? (phase2.financials.cashFlow || 0) - phaseTwoDaily * 365 : 0;
    const yearlyCare = dailyTotal * 365;
    const pension = ready ? careFinancials!.socialSecurity || 0 : 0;
    const pensionToday = ready ? todayFinancials!.socialSecurity || 0 : 0;
    const gap = ready ? (careFinancials!.cashFlow || 0) - yearlyCare : 0;
    const legacy = agedCareBaseline.legacyValue;
    const years = Number(legacyYears) || 0;

    const pensionCaption =
        Math.round(pension) > Math.round(pensionToday)
            ? `Up from ${formatCurrency(pensionToday)} today, once care starts.`
            : Math.round(pension) < Math.round(pensionToday)
                ? `Down from ${formatCurrency(pensionToday)} today, once care starts.`
                : 'The same as today.';

    // The daily cost, line by line — the same lines and the same calculation tooltips the plan
    // summary's cost block carries, so the figure a client taps here is the one an adviser reads there.
    const c = (ready ? costs : null) as
        | { basicDailyFee?: number; dailyAccommodationPayment?: number; dailyNCCC?: number; dailyHSC?: number; dailyMTCF?: number; dailyExtraServiceFee?: number; feeArrangements?: string; calculationBreakdowns?: Record<string, TooltipBreakdownRow[] | undefined> }
        | null;
    const costLines: Array<{ label: string; value: number; breakdown?: TooltipBreakdownRow[] }> = c
        ? [
              { label: 'Basic daily fee', value: c.basicDailyFee || 0, breakdown: c.calculationBreakdowns?.bdf },
              { label: 'Paying for the room day by day (DAP)', value: c.dailyAccommodationPayment || 0, breakdown: c.calculationBreakdowns?.dap },
              ...(c.feeArrangements === '2014'
                  ? [{ label: 'Means-tested care fee (no worse off)', value: c.dailyMTCF || 0, breakdown: c.calculationBreakdowns?.mtcf }]
                  : [
                        { label: 'Care contribution (NCCC)', value: c.dailyNCCC || 0, breakdown: c.calculationBreakdowns?.nccc },
                        { label: 'Everyday living contribution (HSC)', value: c.dailyHSC || 0, breakdown: c.calculationBreakdowns?.hsc },
                    ]),
              ...(c.dailyExtraServiceFee ? [{ label: 'Optional extra services', value: c.dailyExtraServiceFee }] : []),
          ]
        : [];
    const costTooltip: TooltipBreakdownRow[] = costLines.map((l) => ({ label: l.label, value: formatCurrency(l.value) }));

    const sequence = useMemo(
        () => buildMoneySequence((agedCareBaseline.runwayProjections as unknown as RunwayRow[]) || [], factFind, runway),
        [agedCareBaseline.runwayProjections, factFind, runway],
    );

    return (
        <div className="space-y-6">
            {/* 1. The four numbers */}
            <div className={CARD}>
                <p className="text-[11px] font-bold uppercase tracking-[0.1em] text-slate-400">Step 2 of 3</p>
                <h3 className="mt-1 text-[22px] font-bold leading-tight tracking-tight">
                    <GradientText>{existingStay ? stayPutHeading(existingStay) : 'Nothing paid up front, nothing sold. Four numbers tell the story.'}</GradientText>
                </h3>
                {existingStay && (
                    <p className="mt-1 text-[14px] text-slate-600" data-testid="stay-put-intro">{stayPutIntro({ name: enteringName, facilityName: existingStay.facilityName })}</p>
                )}
                {/* "Walk-in position" is the phrase the step 3 rail is headed with, so it is introduced
                    here, in bold, and "baseline" explains it once (owner, 22 Sep 2026). */}
                <p className="mt-1 text-[14px] text-slate-600">
                    Each one is explained beneath. This is the <span className="font-semibold text-slate-800">walk-in position</span>:
                    the baseline every strategy in step 3 is measured against.
                </p>
                {ready && phase2 && (
                    <p className="mt-3 rounded-lg border-l-4 border-blue-500 bg-blue-50/60 px-3 py-2 text-[13px] leading-relaxed text-slate-700" data-testid="phases-intro">
                        {phasesIntro(phaseNames)}
                    </p>
                )}

                {!ready ? (
                    <div className="mt-5 rounded-xl border border-dashed border-slate-300 bg-slate-50/80 px-4 py-6 text-center text-[14px] text-slate-600">
                        {existingStay ? 'Working out the stay-put position…' : 'Enter the room price in step 1 and the four numbers appear here.'}
                    </div>
                ) : (
                    <>
                        <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
                            <Tile
                                label="Care would cost"
                                value={formatCurrency(Math.round(dailyTotal))}
                                unit="a day"
                                caption={phase2 ? `${formatCurrency(Math.round(yearlyCare))} a year. ${phaseOneCaption(phaseNames)}` : `${formatCurrency(Math.round(yearlyCare))} a year, from the day care begins.`}
                                tone="cost"
                                onFigureClick={() => setCostOpen((v) => !v)}
                                figureTooltip={costTooltip}
                                figureOpen={costOpen}
                            />
                            <Tile label={`${enteringName}'s Age Pension`} value={formatCurrency(pension)} unit="a year" caption={pensionCaption} tone="good" />
                            <Tile
                                label={gap < 0 ? 'Left to find each year' : 'Left over each year'}
                                value={`${gap < 0 ? '−' : '+'}${formatCurrency(Math.abs(Math.round(gap)))}`}
                                caption={gap < 0 ? 'After the pension and other income. Met from savings.' : 'After care and living costs are paid.'}
                                tone={gap < 0 ? 'bad' : 'good'}
                            />
                            <Tile
                                label="What's left to the family"
                                value={legacy != null ? formatCurrency(Math.round(legacy)) : '—'}
                                caption={
                                    legacy != null ? (
                                        onLegacyYearsChange ? (
                                            <ProjectionYearsInput years={years} onChange={onLegacyYearsChange} after=": everything owned, less everything owed." />
                                        ) : `After ${years} ${years === 1 ? 'year' : 'years'}: everything owned, less everything owed.`
                                    ) : 'Not available: the no-plan projection could not be run.'
                                }
                                tone="family"
                            />
                        </div>
                        {phase2 && (
                            <div className="mt-4" data-testid="phase-two-row">
                                <p className="text-[11px] font-bold uppercase tracking-[0.1em] text-slate-400">{phaseTwoHeading(phaseNames)}</p>
                                <div className="mt-2 grid grid-cols-1 gap-4 sm:grid-cols-3">
                                    <Tile label="Care would cost" value={formatCurrency(Math.round(phaseTwoDaily))} unit="a day" caption={`${formatCurrency(Math.round(phaseTwoDaily * 365))} a year for both rooms, once ${phaseNames.secondName} moves in.`} tone="cost" />
                                    <Tile label={`${enteringName}'s Age Pension`} value={formatCurrency(phaseTwoPension)} unit="a year" caption={Math.round(phaseTwoPension) === Math.round(pension) ? 'The same as in the first stage.' : `From ${formatCurrency(pension)} in the first stage.`} tone="good" />
                                    <Tile
                                        label={phaseTwoGap < 0 ? 'Left to find each year' : 'Left over each year'}
                                        value={`${phaseTwoGap < 0 ? '−' : '+'}${formatCurrency(Math.abs(Math.round(phaseTwoGap)))}`}
                                        caption={phaseTwoGap < 0 ? 'After the pension and other income, with both rooms paid for.' : 'After both rooms and living costs are paid.'}
                                        tone={phaseTwoGap < 0 ? 'bad' : 'good'}
                                    />
                                </div>
                            </div>
                        )}
                        {costOpen && (
                            <div className="mt-3 rounded-xl border border-slate-200 bg-white px-4 py-3 shadow-card">
                                <p className="text-[11px] font-bold uppercase tracking-[0.09em] text-slate-400">How the {formatCurrency(Math.round(dailyTotal))} a day is made up</p>
                                <div className="mt-2 divide-y divide-slate-100">
                                    {costLines.map((l) => (
                                        <div key={l.label} className="flex items-center justify-between gap-4 py-2 text-[14px]">
                                            <CalculationTooltip title={`${l.label} Calculation`} data={l.breakdown}>
                                                <span className={`text-slate-600 ${l.breakdown ? 'cursor-help underline decoration-dotted decoration-slate-400 underline-offset-2' : ''}`}>{l.label}</span>
                                            </CalculationTooltip>
                                            <span className="font-semibold tabular-nums text-slate-900">{formatCurrency(l.value)}</span>
                                        </div>
                                    ))}
                                    <div className="flex items-center justify-between gap-4 py-2 text-[14px]">
                                        <span className="font-semibold text-slate-900">Total each day</span>
                                        <span className="font-bold tabular-nums text-slate-900">{formatCurrency(dailyTotal)}</span>
                                    </div>
                                </div>
                                <p className="mt-2 text-[13px] text-slate-500">Hover a line for the calculation behind it. Step 3 shows ways to reduce these costs.</p>
                            </div>
                        )}
                        {/* Said once, here, rather than as a tooltip on every figure. */}
                        <p className="mt-3 text-[13px] text-slate-500">
                            Figures use the rates in force from {RATES_PROVENANCE.agePension}. The{' '}
                            <button type="button" onClick={onAssumptions} className="font-semibold text-blue-700 underline decoration-dotted underline-offset-2 hover:text-blue-900">
                                Assumptions
                            </button>{' '}
                            page lists every one, with its source and date.
                        </p>
                    </>
                )}
            </div>

            {/* 2. The story — the app's own plain-words note, unchanged, as its own card at full width */}
            {ready && (
                <div className={CARD}>
                    <div className="-mt-5">
                        <WalkInPositionNote
                            factFind={factFind}
                            radInput={radInput}
                            secondRoom={secondRoom}
                            existingStay={existingStay}
                            villageExit={villageExit}
                            extraServiceFeeInput={extraServiceFeeInput}
                            scenarioLivingExpenses={scenarioLivingExpenses}
                            projectedRunwayYears={runway?.exhaustedYear ?? null}
                            runwayFacts={runwayFactsOf(agedCareBaseline.runwayProjections)}
                        />
                    </div>
                </div>
            )}

            {/* 3. How long the money lasts — this client's pools, in the order they are spent */}
            {ready && runway && (
                <div className={CARD}>
                    <p className="text-[11px] font-bold uppercase tracking-[0.09em] text-slate-400">How long the money lasts</p>
                    <div className="mt-2">
                        <FundingRunway
                            headline={<span className="text-[18px] font-bold leading-tight tracking-tight text-slate-900">{runway.headline}</span>}
                            coveredPct={runway.coveredPct}
                            leftLabel={runway.leftLabel}
                            rightLabel={runway.rightLabel}
                            depleted={runway.depleted}
                        />
                    </div>
                    {/* A two-column grid: the year badges sit in one fixed column, so every line's
                        text starts on the same edge (owner, 22 Sep 2026: "the text starts at different spots"). */}
                    {sequence.length > 0 && (
                        <ol className="mt-4 grid grid-cols-[7.5rem_minmax(0,1fr)] gap-x-3 gap-y-2">
                            {sequence.map((e, i) => (
                                <React.Fragment key={i}>
                                    <li className="list-none"><span className="inline-flex w-full justify-center rounded-md bg-slate-100 px-2 py-1 text-[12px] font-bold uppercase tracking-wide text-slate-600">{e.from}</span></li>
                                    <li className="list-none pt-0.5 text-[14px] leading-relaxed text-slate-600">{e.text}</li>
                                </React.Fragment>
                            ))}
                        </ol>
                    )}
                    {/* Capped at a readable measure and aligned with the timeline's text column, so it
                        wraps to two or three lines rather than running the whole width of the page. */}
                    <HorizonNote runway={runway} className="mt-3 ml-[8.25rem] max-w-3xl" />
                    <button
                        type="button"
                        onClick={onShowProjections}
                        className="mt-3 inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-[13px] font-semibold text-blue-700 hover:border-blue-300 hover:bg-blue-50"
                    >
                        <Eye size={15} /> See the projections, year by year
                    </button>
                </div>
            )}

            <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex flex-wrap items-center gap-3">
                    <button type="button" onClick={onBack} className="inline-flex h-11 items-center gap-2 rounded-lg border border-slate-300 bg-white px-5 text-[14px] font-semibold text-slate-700 shadow-sm hover:bg-slate-50">
                        <ChevronLeft size={16} /> Change the room or start date
                    </button>
                    <button type="button" onClick={onReadGuide} className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-blue-700 hover:text-blue-900">
                        <BookOpen size={15} /> Read the full guide to aged care fees
                    </button>
                </div>
                <button
                    type="button"
                    onClick={onNext}
                    disabled={!ready}
                    className="inline-flex h-11 items-center gap-2 rounded-lg bg-gradient-to-r from-blue-600 to-teal-500 px-5 text-[14px] font-semibold text-white shadow-sm hover:from-blue-700 hover:to-teal-600 disabled:opacity-50"
                >
                    Show me ways to close the gap <ArrowRight size={16} />
                </button>
            </div>
        </div>
    );
};

export default WalkInHero;
